import {
  KIT_SUMMARY_CARDS,
  type KitSummaryCardId,
  type KitSummaryCounts,
} from '@repo/utils';
import c from 'classnames';

import { Icon } from '../Icon';
import { Typography } from '../Typography';
import styles from './SummaryCardFilter.module.css';

/**
 * Shown while the attention card is filtering the table. Rendered by the table
 * rather than here, so it sits below the toolbar as the design has it.
 */
export const NEEDS_ATTENTION_BANNER_CONTENT =
  'These results cannot be released until kit owners activate their kits and/or complete their surveys.';

interface CardDisplay {
  label: string;
  dotClassName: string;
  /** Only the attention card flags its count with an alert icon. */
  showsAlertIcon?: boolean;
}

const CARD_DISPLAY: Record<KitSummaryCardId, CardDisplay> = {
  needsAttention: {
    label: 'Needs attention',
    dotClassName: styles.dotRed,
    showsAlertIcon: true,
  },
  resultsReady: {
    label: 'Results ready',
    dotClassName: styles.dotOrange,
  },
  releasedLast30Days: {
    label: 'Results released in last 30 days',
    dotClassName: styles.dotGreen,
  },
};

export interface SummaryCardFilterProps {
  /** Totals across every kit, independent of any filter on the table. */
  counts?: KitSummaryCounts;
  isLoading?: boolean;
  activeCard: KitSummaryCardId | null;
  /** `null` clears the card filter and restores the full table. */
  onSelectCard: (card: KitSummaryCardId | null) => void;
  className?: string;
}

/**
 * The summary cards above the kits table: one card per at-a-glance kit group,
 * each filtering the table to its own kits. Only one card can be active at a
 * time, so selecting one replaces whatever was selected before. `KitsTableSummary`
 * owns the "Summary" heading and any banners above these, so that they survive the
 * kits_table_summary flag that gates the cards.
 */
export const SummaryCardFilter = ({
  counts,
  isLoading,
  activeCard,
  onSelectCard,
  className,
}: SummaryCardFilterProps) => {
  return (
    <div className={c(styles.section, className)}>
      <div className={styles.cardGrid}>
        {KIT_SUMMARY_CARDS.map(card => {
          const display = CARD_DISPLAY[card];
          const count = counts?.[card];
          const isActive = activeCard === card;
          const isInactive = activeCard !== null && !isActive;
          // Nothing to look at, so the card offers no way in.
          const isEmpty = !isLoading && count === 0;

          return (
            <div
              key={card}
              className={c(
                styles.card,
                isActive && styles.cardActive,
                isInactive && styles.cardInactive,
              )}
            >
              <div className={styles.cardHeader}>
                <div className={styles.cardLabel}>
                  <span className={c(styles.dot, display.dotClassName)} />
                  <Typography variant='body-s'>{display.label}</Typography>
                </div>
                <button
                  type='button'
                  className={styles.viewButton}
                  aria-pressed={isActive}
                  disabled={isLoading || isEmpty || isInactive}
                  onClick={() => onSelectCard(isActive ? null : card)}
                >
                  <Typography variant='body-s'>
                    {isActive ? 'Done' : 'View'}
                  </Typography>
                </button>
              </div>
              <div className={styles.cardCount}>
                <Typography variant='heading-l'>
                  {isLoading ? '—' : (count ?? 0)}
                </Typography>
                {display.showsAlertIcon && !isLoading && !isEmpty && (
                  <Icon
                    name='alertTriangleFilled'
                    size='s'
                    className={styles.alertIcon}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
      <hr className={styles.divider} />
    </div>
  );
};

export default SummaryCardFilter;
